/* eslint-disable no-case-declarations */
import parse from 'html-react-parser';
import React, { forwardRef } from 'react';

import { tw } from '../../utils/tailwind';
import { TEXT_STYLES } from './constants';

interface CustomTextProps {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  children: string | undefined | null | string[];
  className?: string;
  style?: React.CSSProperties;
  variant?: TEXT_STYLES;
  isProse?: boolean;
  id?: string;
}

const CustomText: FCWithClassName<CustomTextProps> = forwardRef<
  HTMLHeadingElement,
  CustomTextProps
>((props, forwardedRef) => {
  const {
    as,
    children,
    className,
    style,
    variant = TEXT_STYLES.P,
    isProse = false,
  } = props;
  if (!children) return null;
  const normalizeString = (childrenContent: object | string) => {
    if (typeof childrenContent === 'object') return children;
    const trimmedChildren = childrenContent.trim().endsWith('\n')
      ? childrenContent.trim().slice(0, -2)
      : childrenContent.trim();
    if (
      trimmedChildren.slice(0, 3) === '<p>' &&
      trimmedChildren.slice(
        trimmedChildren.length - 4,
        trimmedChildren.length
      ) === '</p>'
    ) {
      return parse(trimmedChildren.slice(3, trimmedChildren.length - 4));
    }
    return parse(trimmedChildren);
  };
  const normalizedChildren = normalizeString(children);
  let Tag = as;
  switch (variant) {
    case TEXT_STYLES.HEADLINE:
    case TEXT_STYLES.H1:
      Tag = as ?? 'h1';
      return (
        <Tag
          ref={forwardedRef}
          className={tw(
            'font-heading text-6xl lg:text-[80px] leading-none tracking-normal uppercase max-w-[20em] font-bold',
            className
          )}
          style={style}
          id={props.id}
        >
          {normalizedChildren}
        </Tag>
      );
    case TEXT_STYLES.SECTION:
    case TEXT_STYLES.H2:
      Tag = as ?? 'h2';
      return (
        <Tag
          className={tw(
            'font-heading text-6 lg:text-5xl leading-none tracking-normal uppercase max-w-[20em] font-black',
            className
          )}
          style={style}
          ref={forwardedRef}
          id={props.id}
        >
          {normalizedChildren}
        </Tag>
      );
    case TEXT_STYLES.TAGLINE:
      Tag = as ?? 'h3';
      return (
        <Tag
          className={tw(
            'font-heading text-2xl lg:text-4xl font-bold  leading-none tracking-normal uppercase max-w-[20em]',
            className
          )}
          style={style}
          ref={forwardedRef}
          id={props.id}
        >
          {normalizedChildren}
        </Tag>
      );
    case TEXT_STYLES.H3:
      Tag = as ?? 'h3';
      return (
        <Tag
          className={tw(
            'font-heading text-2xl lg:text-6 leading-tight tracking-normal uppercase max-w-[45em] font-normal',
            className
          )}
          style={style}
          ref={forwardedRef}
          id={props.id}
        >
          {normalizedChildren}
        </Tag>
      );
    case TEXT_STYLES.H4:
      Tag = as ?? 'h4';
      return (
        <Tag
          className={tw(
            'font-heading font-bold text-lg md:text-5 leading-tight tracking-normal uppercase max-w-[45em]',
            className
          )}
          style={style}
          ref={forwardedRef}
          id={props.id}
        >
          {normalizedChildren}
        </Tag>
      );
    case TEXT_STYLES.H5:
      Tag = as ?? 'h5';
      return (
        <Tag
          className={tw(
            'font-heading text-lg md:text-5 leading-normal max-w-[45em] font-normal',
            className
          )}
          style={style}
          ref={forwardedRef}
          id={props.id}
        >
          {normalizedChildren}
        </Tag>
      );
    case TEXT_STYLES.P:
      // isProse means that content is coming from WYSIWYG, therefore we don't want to wrap it with a p because it's invalid HTML
      Tag = as ?? isProse ? 'div' : 'p';
      return (
        <Tag
          className={tw(
            className ?? '',
            isProse ? 'prose' : '',
            'font-body text-lg max-w-[45em]'
          )}
          style={style}
          ref={forwardedRef}
          id={props.id}
        >
          {normalizedChildren}
        </Tag>
      );

    default: {
      const exhaustiveCheck: never = variant;
      throw new Error(
        `There is a case missing in the switch. Unexpected: ${exhaustiveCheck}`
      );
    }
  }
});

export default CustomText;
CustomText.displayName = 'CustomText';
